import { ArrowLeft, Mail, Send } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Card } from "../../components/ui/card";
import { useNavigate } from "react-router-dom";
import welcomeBg from "../../assets/homebg.png";
import { useState } from "react";

const ContactSupportPage = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    category: "",
    subject: "",
    message: "",
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Handle form submission here
  };

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  return (
    <div
      className="scrollbar-hide relative mx-auto w-full max-w-[400px] bg-background"
      style={{
        minHeight: "100vh",
        maxHeight: "100vh",
        overflow: "hidden",
      }}
    >
      {/* Blurred Background Image */}
      <div
        className="absolute inset-0 scale-105 bg-cover bg-center bg-no-repeat blur-md"
        style={{
          backgroundImage: `url(${welcomeBg})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
        }}
      />

      {/* Overlay for better contrast */}
      <div className="absolute inset-0 bg-background/80" />

      <div
        className="scrollbar-hide relative z-10 space-y-6 overflow-y-auto p-4 pb-16"
        style={{ height: "calc(100vh - 0px)" }}
      >
        {/* Header */}
        <div className="flex items-center gap-4 pt-2">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate(-1)}
            className="rounded-xl bg-card/80 hover:bg-card"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <h1 className="text-title2 font-bold text-foreground">Contact Support</h1>
        </div>

        {/* Email Support Info */}
        <Card className="flex items-center gap-4 rounded-2xl border border-border bg-card p-4 shadow-sm">
          <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-xl bg-blue-50">
            <Mail className="h-6 w-6 text-blue-600" />
          </div>
          <div>
            <h3 className="font-semibold text-foreground">Email Support</h3>
            <p className="text-subhead text-muted-foreground">
              We typically respond within 24-48 hours
            </p>
          </div>
        </Card>

        {/* Contact Form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Your Name */}
          <div className="space-y-2">
            <label className="text-subhead font-medium text-foreground">
              Your Name
            </label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder="Enter your name"
              className="w-full rounded-xl border border-border bg-card px-4 py-3 text-subhead text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Email Address */}
          <div className="space-y-2">
            <label className="text-subhead font-medium text-foreground">
              Email Address
            </label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="Enter your email"
              className="w-full rounded-xl border border-border bg-card px-4 py-3 text-subhead text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Category */}
          <div className="space-y-2">
            <label className="text-subhead font-medium text-foreground">
              Category <span className="text-red-500">*</span>
            </label>
            <select
              name="category"
              value={formData.category}
              onChange={handleChange}
              required
              className="w-full rounded-xl border border-border bg-card px-4 py-3 text-subhead text-foreground focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Select a category</option>
              <option value="technical">Technical Issue</option>
              <option value="account">Account & Billing</option>
              <option value="feature">Feature Request</option>
              <option value="data">Data & Privacy</option>
              <option value="general">General Question</option>
              <option value="other">Other</option>
            </select>
          </div>

          {/* Subject */}
          <div className="space-y-2">
            <label className="text-subhead font-medium text-foreground">
              Subject <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="subject"
              value={formData.subject}
              onChange={handleChange}
              placeholder="Brief description of your issue"
              required
              className="w-full rounded-xl border border-border bg-card px-4 py-3 text-subhead text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Message */}
          <div className="space-y-2">
            <label className="text-subhead font-medium text-foreground">
              Message <span className="text-red-500">*</span>
            </label>
            <textarea
              name="message"
              value={formData.message}
              onChange={handleChange}
              placeholder="Please describe your issue or question in detail..."
              required
              rows={5}
              className="w-full resize-none rounded-xl border border-border bg-card px-4 py-3 text-subhead text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Submit Button */}
          <Button
            type="submit"
            className="w-full rounded-xl bg-gradient-primary py-6 text-callout font-semibold text-primary-foreground shadow-primary hover:opacity-90"
          >
            <Send className="mr-2 h-5 w-5" />
            Send Message
          </Button>
        </form>

        {/* Footer Note */}
        <p className="text-center text-subhead text-muted-foreground">
          For urgent issues, please include &quot;URGENT&quot; in your subject
          line.
        </p>
      </div>
    </div>
  );
};

export default ContactSupportPage;
